/**
 * Hard-coded sample responses for Reims (MEF HQ) — served in place of a live
 * API call when the visitor isn't signed in. Lets a new user click around the
 * map and see the response shape without burning anyone's quota.
 *
 * Values are realistic for Champagne-region calcareous soils (chalk plateau:
 * high CaCO3, slightly alkaline pH, clay-loam texture). Layer codes,
 * categorical IDs, and the admin GID match what `seed_plans` + GADM + the
 * catalog produce against a fully-ingested dev DB. Re-snapshot if the schema
 * changes substantially.
 */

import type {
  AdminPolygon, PointResponse, ZoneResponse,
} from "../types/api";

const ADMIN = {
  level: 5,
  level_label: "ADM_5",
  country: "FRA",
  name: "Reims",
  gid: "FRA.6.6.3.10.1_1",
};

export const REIMS_POLYGON: AdminPolygon = {
  gid: "FRA.6.6.3.10.1_1",
  level: 5,
  name: "Reims",
  geometry: {
    type: "Polygon",
    coordinates: [
      [
        [4.08109, 49.27285], [4.08106, 49.26291], [4.09172, 49.24863],
        [4.09049, 49.23605], [4.09085, 49.23027], [4.12341, 49.22013],
        [4.11653, 49.21305], [4.10266, 49.22342], [4.08831, 49.22059],
        [4.0727, 49.22375], [4.04661, 49.2274], [4.03061, 49.21386],
        [4.02705, 49.2041], [4.01776, 49.20469], [4.01759, 49.2108],
        [4.01116, 49.21302], [4.00786, 49.21552], [4.012, 49.22155],
        [4.00957, 49.22445], [3.99699, 49.22981], [3.99876, 49.24248],
        [4.00681, 49.24601], [4.01057, 49.25187], [3.99837, 49.25553],
        [3.99581, 49.26163], [4.00445, 49.28074], [3.99701, 49.2844],
        [3.9931, 49.28223], [3.98879, 49.29397], [3.99651, 49.29797],
        [4.01032, 49.3021], [4.01842, 49.29804], [4.02861, 49.29764],
        [4.02774, 49.28723], [4.04511, 49.27063], [4.05035, 49.27008],
        [4.06828, 49.27698], [4.08309, 49.27468], [4.08109, 49.27285],
      ],
    ],
  },
  bbox: [3.98878693, 49.20410156, 4.12341208, 49.30209732],
};

export const REIMS_POINT: PointResponse = {
  lat: 49.2583,
  lon: 4.0317,
  admin: ADMIN,
  soil: [
    { code: "bdod",     value: 1.42,  unit: "g/cm³", q05: 1.22, q95: 1.61 },
    { code: "caco3",    value: 241,   unit: "g/kg",  source: "High-resolution survey (INRAE WMS)" },
    { code: "cfvo",     value: 11.6,  unit: "%",     q05: 4.2,  q95: 22.3 },
    { code: "clay",     value: 282,   unit: "g/kg",  q05: 215,  q95: 348 },
    { code: "nitrogen", value: 2.07,  unit: "g/kg",  q05: 1.41, q95: 2.74 },
    { code: "ocs",      value: 58.9,  unit: "t/ha",  q05: 39.2, q95: 81.4 },
    { code: "phh2o",    value: 7.84,  unit: "pH",    q05: 7.35, q95: 8.24 },
    { code: "sand",     value: 247,   unit: "g/kg",  q05: 168,  q95: 332 },
    { code: "silt",     value: 471,   unit: "g/kg",  q05: 392,  q95: 552 },
    { code: "soc",      value: 24.6,  unit: "g/kg",  q05: 15.3, q95: 35.8 },
  ],
  weather: [
    { code: "ppt",   value: 612, unit: "mm" },
    { code: "tmin",  value: 6.1, unit: "°C" },
    { code: "tmax",  value: 14.3, unit: "°C" },
    { code: "tmean", value: 10.2, unit: "°C" },
    { code: "pet",   value: 698, unit: "mm" },
  ],
  climate: [
    { code: "ipcc_climate", value: 3, label: "Cool Temperate Moist", unit: null },
    { code: "ipcc_soil",    value: 6, label: "HAC soils",            unit: null },
    { code: "cft_climate",  value: 7, label: "Cool Temperate Moist", unit: null },
    { code: "cft_soil",     value: 5, label: "Volcanic",             unit: null },
  ],
  landuse: [
    { code: "worldcover",          value: 40, label: "Cropland", unit: null },
    { code: "treecover_2000_pct",  value: 4,  unit: "%" },
    { code: "lossyear",            value: 0,  label: "No loss", unit: null },
    { code: "biomass_ag_mgha",     value: 12, unit: "Mg/ha" },
    { code: "carbon_ag_tha",       value: 6,  unit: "tC/ha",
      source: "biomass × 0.47 (IPCC default carbon fraction)" },
  ],
  forest_patch: null,
  regrowth_patch: null,
  weather_series: null,
  soil_texture: [
    { scheme: "USDA",          class_id: 9,  class_name: "Clay Loam" },
    { scheme: "FAO",           class_id: 2,  class_name: "Medium" },
    { scheme: "INTERNATIONAL", class_id: 6,  class_name: "Silty Clay Loam" },
    { scheme: "ISSS",          class_id: 4,  class_name: "Silty Clay" },
  ],
  warnings: null,
};

export const REIMS_ZONE: ZoneResponse = {
  lat: 49.2583,
  lon: 4.0317,
  admin: ADMIN,
  soil: [
    { code: "bdod",     min: 1.34, max: 1.51, mean: 1.41, unit: "g/cm³", q05: 1.21, q95: 1.62 },
    { code: "caco3",    min: 198,  max: 287,  mean: 238,  unit: "g/kg" },
    { code: "cfvo",     min: 5.4,  max: 18.7, mean: 12.0, unit: "%",     q05: 3.9,  q95: 23.5 },
    { code: "clay",     min: 251,  max: 314,  mean: 285,  unit: "g/kg",  q05: 212,  q95: 352 },
    { code: "nitrogen", min: 1.62, max: 2.45, mean: 2.04, unit: "g/kg",  q05: 1.38, q95: 2.72 },
    { code: "ocs",      min: 41.5, max: 75.2, mean: 57.6, unit: "t/ha",  q05: 38.1, q95: 80.7 },
    { code: "phh2o",    min: 7.62, max: 8.01, mean: 7.82, unit: "pH",    q05: 7.31, q95: 8.27 },
    { code: "sand",     min: 213,  max: 289,  mean: 250,  unit: "g/kg",  q05: 166,  q95: 335 },
    { code: "silt",     min: 432,  max: 511,  mean: 472,  unit: "g/kg",  q05: 389,  q95: 555 },
    { code: "soc",      min: 18.2, max: 31.4, mean: 24.1, unit: "g/kg",  q05: 14.9, q95: 36.2 },
  ],
  weather: [
    { code: "ppt",   min: 588, max: 641, mean: 615, unit: "mm" },
    { code: "tmin",  min: 5.7, max: 6.5, mean: 6.1, unit: "°C" },
    { code: "tmax",  min: 13.8, max: 14.9, mean: 14.3, unit: "°C" },
    { code: "tmean", min: 9.8, max: 10.7, mean: 10.2, unit: "°C" },
    { code: "pet",   min: 678, max: 717, mean: 698, unit: "mm" },
  ],
  climate: [
    { code: "ipcc_climate", min: null, max: null, mean: null, unit: null, mode: 3, mode_label: "Cool Temperate Moist" },
    { code: "ipcc_soil",    min: null, max: null, mean: null, unit: null, mode: 6, mode_label: "HAC soils" },
    { code: "cft_climate",  min: null, max: null, mean: null, unit: null, mode: 7, mode_label: "Cool Temperate Moist" },
    { code: "cft_soil",     min: null, max: null, mean: null, unit: null, mode: 5, mode_label: "Volcanic" },
  ],
  landuse: [
    { code: "worldcover",         min: null, max: null, mean: null, unit: null, mode: 40, mode_label: "Cropland" },
    { code: "treecover_2000_pct", min: 0,    max: 87,   mean: 8.4,  unit: "%" },
    { code: "lossyear",           min: null, max: null, mean: null, unit: null, mode: 0,  mode_label: "No loss" },
    { code: "biomass_ag_mgha",    min: 4,    max: 142,  mean: 18,   unit: "Mg/ha" },
    { code: "carbon_ag_tha",      min: 1.88, max: 66.74, mean: 8.46, unit: "tC/ha" },
  ],
  forest_change: {
    cover2000_mean_pct: 8.4,
    pre_forest_area_ha: 412.3,
    still_forested_area_ha: 408.9,
    total_loss_area_ha: 3.4,
    post_eudr_loss_area_ha: 0.0,
    loss_by_year: [
      { year: 2008, ha: 0.9 },
      { year: 2013, ha: 1.4 },
      { year: 2017, ha: 1.1 },
    ],
    pixel_count: 56_321,
    data_end_year: 2023,
    truncated: false,
  },
  forest_patches: null,
  regrowth_patches: null,
  weather_series: null,
  soil_texture: [
    { scheme: "USDA",          class_id: 9,  class_name: "Clay Loam" },
    { scheme: "FAO",           class_id: 2,  class_name: "Medium" },
    { scheme: "INTERNATIONAL", class_id: 6,  class_name: "Silty Clay Loam" },
    { scheme: "ISSS",          class_id: 4,  class_name: "Silty Clay" },
  ],
  warnings: null,
};
